import { useState } from "react";
import PropTypes from "prop-types";
import { FiRefreshCw, FiAlertCircle, FiCheck, FiX } from "react-icons/fi";
import {
  identifyFailedRequests,
  triggerRerun,
} from "../../services/runService";

/**
 * Re-run Button Component
 * Displays a button to re-run failed requests from a benchmark run.
 * Shows modal with failed request details across three categories:
 *   - Generation errors (will regenerate + evaluate)
 *   - Evaluation errors (will re-evaluate only)
 *   - Failed questions (scored below pass threshold, will re-evaluate only)
 */
const RerunButton = ({ run, onRerunStarted }) => {
  const [showModal, setShowModal] = useState(false);
  const [isRerunning, setIsRerunning] = useState(false);
  const [rerunError, setRerunError] = useState(null);
  const [rerunSuccess, setRerunSuccess] = useState(false);
  const [selectedRequests, setSelectedRequests] = useState(new Set());
  const [activeTab, setActiveTab] = useState("generation");

  const isRunningRerun =
    run &&
    ["queued", "generating", "evaluating"].includes(run.status) &&
    (run.raw_generation_results || run.raw_evaluation_results);

  const canRerun =
    run &&
    (run.status === "completed" || run.status === "failed" || isRunningRerun);

  const failedRequests = canRerun
    ? identifyFailedRequests(
        run.raw_generation_results,
        run.raw_evaluation_results,
        run.raw_requests
      )
    : {
        failedGeneration: [],
        failedEvaluation: [],
        failedQuestions: [],
        totalFailed: 0,
      };

  const hasFailedRequests = failedRequests.totalFailed > 0;

  const handleOpenModal = () => {
    const allFailedIds = [
      ...failedRequests.failedGeneration.map((r) => r.requestId),
      ...failedRequests.failedEvaluation.map((r) => r.requestId),
      ...failedRequests.failedQuestions.map((r) => r.requestId),
    ];
    setSelectedRequests(new Set(allFailedIds));
    if (failedRequests.failedGeneration.length > 0) {
      setActiveTab("generation");
    } else if (failedRequests.failedEvaluation.length > 0) {
      setActiveTab("evaluation");
    } else if (failedRequests.failedQuestions.length > 0) {
      setActiveTab("failedQuestions");
    }
    setShowModal(true);
  };

  const toggleRequest = (requestId) => {
    const newSelected = new Set(selectedRequests);
    if (newSelected.has(requestId)) {
      newSelected.delete(requestId);
    } else {
      newSelected.add(requestId);
    }
    setSelectedRequests(newSelected);
  };

  const toggleAll = () => {
    if (selectedRequests.size === failedRequests.totalFailed) {
      setSelectedRequests(new Set());
    } else {
      const allFailedIds = [
        ...failedRequests.failedGeneration.map((r) => r.requestId),
        ...failedRequests.failedEvaluation.map((r) => r.requestId),
        ...failedRequests.failedQuestions.map((r) => r.requestId),
      ];
      setSelectedRequests(new Set(allFailedIds));
    }
  };

  const selectAllGeneration = () => {
    const generationIds = failedRequests.failedGeneration.map(
      (r) => r.requestId
    );
    const newSelected = new Set(selectedRequests);
    generationIds.forEach((id) => newSelected.add(id));
    setSelectedRequests(newSelected);
  };

  const deselectAllGeneration = () => {
    const generationIds = new Set(
      failedRequests.failedGeneration.map((r) => r.requestId)
    );
    const newSelected = new Set(
      [...selectedRequests].filter((id) => !generationIds.has(id))
    );
    setSelectedRequests(newSelected);
  };

  const selectAllEvaluation = () => {
    const evaluationIds = failedRequests.failedEvaluation.map(
      (r) => r.requestId
    );
    const newSelected = new Set(selectedRequests);
    evaluationIds.forEach((id) => newSelected.add(id));
    setSelectedRequests(newSelected);
  };

  const deselectAllEvaluation = () => {
    const evaluationIds = new Set(
      failedRequests.failedEvaluation.map((r) => r.requestId)
    );
    const newSelected = new Set(
      [...selectedRequests].filter((id) => !evaluationIds.has(id))
    );
    setSelectedRequests(newSelected);
  };

  const selectAllFailedQuestions = () => {
    const questionIds = failedRequests.failedQuestions.map((r) => r.requestId);
    const newSelected = new Set(selectedRequests);
    questionIds.forEach((id) => newSelected.add(id));
    setSelectedRequests(newSelected);
  };

  const deselectAllFailedQuestions = () => {
    const questionIds = new Set(
      failedRequests.failedQuestions.map((r) => r.requestId)
    );
    const newSelected = new Set(
      [...selectedRequests].filter((id) => !questionIds.has(id))
    );
    setSelectedRequests(newSelected);
  };

  const allGenerationSelected =
    failedRequests.failedGeneration.length > 0 &&
    failedRequests.failedGeneration.every((r) =>
      selectedRequests.has(r.requestId)
    );

  const allEvaluationSelected =
    failedRequests.failedEvaluation.length > 0 &&
    failedRequests.failedEvaluation.every((r) =>
      selectedRequests.has(r.requestId)
    );

  const allFailedQuestionsSelected =
    failedRequests.failedQuestions.length > 0 &&
    failedRequests.failedQuestions.every((r) =>
      selectedRequests.has(r.requestId)
    );

  if (!canRerun || (!hasFailedRequests && !isRunningRerun)) {
    return null;
  }

  const handleRerun = async () => {
    if (selectedRequests.size === 0) {
      setRerunError("Please select at least one request to re-run");
      return;
    }

    setIsRerunning(true);
    setRerunError(null);
    setRerunSuccess(false);

    try {
      const requestIds = Array.from(selectedRequests);
      const result = await triggerRerun(run.id, requestIds);

      if (result.success) {
        setRerunSuccess(true);
        setTimeout(() => {
          setShowModal(false);
          setRerunSuccess(false);
          setSelectedRequests(new Set());
          if (onRerunStarted) {
            onRerunStarted();
          }
        }, 2000);
      } else {
        setRerunError(result.error?.message || "Failed to trigger re-run");
      }
    } catch (error) {
      const errorMsg = error.message || "An unexpected error occurred";
      setRerunError(errorMsg);

      if (errorMsg.includes("must be completed or failed")) {
        setRerunError(
          "This run is still in progress. Please wait for it to complete before re-running. Try refreshing the page."
        );
      }
    } finally {
      setIsRerunning(false);
    }
  };

  return (
    <>
      {isRunningRerun ? (
        <button
          disabled
          className="flex cursor-not-allowed items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 opacity-75"
          title="Re-run in progress"
        >
          <FiRefreshCw className="size-4 animate-spin" />
          <span>Re-run Running</span>
        </button>
      ) : (
        <button
          onClick={handleOpenModal}
          className="flex items-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-4 py-2 text-sm font-medium text-orange-700 transition-all hover:border-orange-300 hover:bg-orange-100 hover:shadow-md active:scale-95"
          title="Re-run failed requests"
        >
          <FiRefreshCw className="size-4" />
          Re-run Failed ({failedRequests.totalFailed})
        </button>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <button
            type="button"
            className="absolute inset-0 cursor-default"
            onClick={() => !isRerunning && setShowModal(false)}
            disabled={isRerunning}
          />

          <div className="relative w-full max-w-4xl rounded-xl border border-gray-200 bg-white shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-lg bg-orange-100">
                  <FiRefreshCw className="size-5 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Re-run Failed Requests
                  </h3>
                  <p className="text-sm text-gray-500">
                    {selectedRequests.size} of {failedRequests.totalFailed}{" "}
                    request
                    {failedRequests.totalFailed !== 1 ? "s" : ""} selected
                  </p>
                </div>
              </div>
              {!isRerunning && (
                <button
                  onClick={() => setShowModal(false)}
                  className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                >
                  <FiX className="size-5" />
                </button>
              )}
            </div>

            {/* Global Select All / Deselect All */}
            <div className="border-b border-gray-200 bg-gray-50 px-6 py-3">
              <label className="flex cursor-pointer items-center justify-between">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={
                      selectedRequests.size === failedRequests.totalFailed
                    }
                    onChange={toggleAll}
                    className="size-4 cursor-pointer rounded border-gray-300 text-orange-600 focus:ring-2 focus:ring-orange-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {selectedRequests.size === failedRequests.totalFailed
                      ? "Deselect All"
                      : "Select All"}
                  </span>
                </div>
                <span className="text-sm text-gray-500">
                  {selectedRequests.size} of {failedRequests.totalFailed}{" "}
                  selected
                </span>
              </label>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 px-6">
              <div className="flex gap-1">
                {failedRequests.failedGeneration.length > 0 && (
                  <button
                    onClick={() => setActiveTab("generation")}
                    className={`relative px-4 py-3 text-sm font-medium transition-colors ${
                      activeTab === "generation"
                        ? "text-red-700"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      Generation Errors
                      <span
                        className={`flex size-5 items-center justify-center rounded-full text-xs font-bold ${
                          activeTab === "generation"
                            ? "bg-red-100 text-red-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {failedRequests.failedGeneration.length}
                      </span>
                    </span>
                    {activeTab === "generation" && (
                      <div className="absolute inset-x-0 bottom-0 h-0.5 bg-red-600"></div>
                    )}
                  </button>
                )}
                {failedRequests.failedEvaluation.length > 0 && (
                  <button
                    onClick={() => setActiveTab("evaluation")}
                    className={`relative px-4 py-3 text-sm font-medium transition-colors ${
                      activeTab === "evaluation"
                        ? "text-yellow-700"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      Evaluation Errors
                      <span
                        className={`flex size-5 items-center justify-center rounded-full text-xs font-bold ${
                          activeTab === "evaluation"
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {failedRequests.failedEvaluation.length}
                      </span>
                    </span>
                    {activeTab === "evaluation" && (
                      <div className="absolute inset-x-0 bottom-0 h-0.5 bg-yellow-600"></div>
                    )}
                  </button>
                )}
                {failedRequests.failedQuestions.length > 0 && (
                  <button
                    onClick={() => setActiveTab("failedQuestions")}
                    className={`relative px-4 py-3 text-sm font-medium transition-colors ${
                      activeTab === "failedQuestions"
                        ? "text-purple-700"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      Failed Questions
                      <span
                        className={`flex size-5 items-center justify-center rounded-full text-xs font-bold ${
                          activeTab === "failedQuestions"
                            ? "bg-purple-100 text-purple-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {failedRequests.failedQuestions.length}
                      </span>
                    </span>
                    {activeTab === "failedQuestions" && (
                      <div className="absolute inset-x-0 bottom-0 h-0.5 bg-purple-600"></div>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="max-h-[500px] overflow-y-auto px-6 py-4">
              {/* Failed Generation Tab */}
              {activeTab === "generation" &&
                failedRequests.failedGeneration.length > 0 && (
                  <div>
                    <div className="mb-4 flex items-center justify-between">
                      <p className="text-sm text-gray-600">
                        These requests will be regenerated and then evaluated.
                      </p>
                      <button
                        onClick={
                          allGenerationSelected
                            ? deselectAllGeneration
                            : selectAllGeneration
                        }
                        className={`shrink-0 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                          allGenerationSelected
                            ? "border-red-300 bg-red-100 text-red-700 hover:bg-red-200"
                            : "border-red-200 bg-white text-red-600 hover:bg-red-50"
                        }`}
                      >
                        {allGenerationSelected ? "Deselect" : "Select"} All (
                        {failedRequests.failedGeneration.length})
                      </button>
                    </div>
                    <div className="space-y-2">
                      {failedRequests.failedGeneration.map((item, idx) => (
                        <label
                          key={idx}
                          htmlFor={`gen-${item.requestId}`}
                          className="flex cursor-pointer items-start gap-3 rounded-lg border border-red-100 bg-red-50 p-4 transition-colors hover:border-red-200 hover:bg-red-100"
                        >
                          <input
                            id={`gen-${item.requestId}`}
                            type="checkbox"
                            checked={selectedRequests.has(item.requestId)}
                            onChange={() => toggleRequest(item.requestId)}
                            className="mt-0.5 size-4 cursor-pointer rounded border-gray-300 text-orange-600 focus:ring-2 focus:ring-orange-500"
                            aria-label={`Select failed generation request ${item.requestId}`}
                          />
                          <div className="flex-1">
                            <div className="mb-1 font-mono text-sm font-medium text-red-900">
                              {item.requestId}
                            </div>
                            <div className="text-sm text-red-700">
                              {item.error || "Unknown error"}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

              {/* Failed Evaluation Tab */}
              {activeTab === "evaluation" &&
                failedRequests.failedEvaluation.length > 0 && (
                  <div>
                    <div className="mb-4 flex items-center justify-between">
                      <p className="text-sm text-gray-600">
                        These items will reuse existing generated content and
                        only re-evaluate. Includes items with evaluation errors
                        and empty/blank content (0% score).
                      </p>
                      <button
                        onClick={
                          allEvaluationSelected
                            ? deselectAllEvaluation
                            : selectAllEvaluation
                        }
                        className={`shrink-0 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                          allEvaluationSelected
                            ? "border-yellow-300 bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                            : "border-yellow-200 bg-white text-yellow-600 hover:bg-yellow-50"
                        }`}
                      >
                        {allEvaluationSelected ? "Deselect" : "Select"} All (
                        {failedRequests.failedEvaluation.length})
                      </button>
                    </div>
                    <div className="space-y-2">
                      {failedRequests.failedEvaluation.map((item, idx) => (
                        <label
                          key={idx}
                          htmlFor={`eval-${item.requestId}`}
                          className="flex cursor-pointer items-start gap-3 rounded-lg border border-yellow-100 bg-yellow-50 p-4 transition-colors hover:border-yellow-200 hover:bg-yellow-100"
                        >
                          <input
                            id={`eval-${item.requestId}`}
                            type="checkbox"
                            checked={selectedRequests.has(item.requestId)}
                            onChange={() => toggleRequest(item.requestId)}
                            className="mt-0.5 size-4 cursor-pointer rounded border-gray-300 text-orange-600 focus:ring-2 focus:ring-orange-500"
                            aria-label={`Select failed evaluation request ${item.requestId}`}
                          />
                          <div className="flex-1">
                            <div className="mb-1 font-mono text-sm font-medium text-yellow-900">
                              {item.requestId}
                            </div>
                            <div className="text-sm text-yellow-700">
                              {item.error === "Empty or 0% content" ? (
                                <span className="flex items-center gap-1">
                                  <FiAlertCircle className="size-3" />
                                  Empty/blank content (scored 0%)
                                </span>
                              ) : (
                                item.error || "Unknown error"
                              )}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

              {/* Failed Questions Tab */}
              {activeTab === "failedQuestions" &&
                failedRequests.failedQuestions.length > 0 && (
                  <div>
                    <div className="mb-4 flex items-center justify-between">
                      <p className="text-sm text-gray-600">
                        These items scored below the 85% pass threshold. They
                        will reuse existing generated content and only
                        re-evaluate.
                      </p>
                      <button
                        onClick={
                          allFailedQuestionsSelected
                            ? deselectAllFailedQuestions
                            : selectAllFailedQuestions
                        }
                        className={`shrink-0 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                          allFailedQuestionsSelected
                            ? "border-purple-300 bg-purple-100 text-purple-700 hover:bg-purple-200"
                            : "border-purple-200 bg-white text-purple-600 hover:bg-purple-50"
                        }`}
                      >
                        {allFailedQuestionsSelected ? "Deselect" : "Select"} All
                        ({failedRequests.failedQuestions.length})
                      </button>
                    </div>
                    <div className="space-y-2">
                      {failedRequests.failedQuestions.map((item, idx) => (
                        <label
                          key={idx}
                          htmlFor={`fq-${item.requestId}`}
                          className="flex cursor-pointer items-start gap-3 rounded-lg border border-purple-100 bg-purple-50 p-4 transition-colors hover:border-purple-200 hover:bg-purple-100"
                        >
                          <input
                            id={`fq-${item.requestId}`}
                            type="checkbox"
                            checked={selectedRequests.has(item.requestId)}
                            onChange={() => toggleRequest(item.requestId)}
                            className="mt-0.5 size-4 cursor-pointer rounded border-gray-300 text-orange-600 focus:ring-2 focus:ring-orange-500"
                            aria-label={`Select failed question ${item.requestId}`}
                          />
                          <div className="flex-1">
                            <div className="mb-1 flex items-center gap-2 font-mono text-sm font-medium text-purple-900">
                              {item.requestId}
                              <span className="rounded bg-purple-200 px-1.5 py-0.5 text-xs font-semibold text-purple-800">
                                {Math.round((item.score ?? 0) * 100)}%
                              </span>
                            </div>
                            <div className="text-sm text-purple-700">
                              Scored below pass threshold (85%)
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

              {/* Success Message */}
              {rerunSuccess && (
                <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-4">
                  <div className="flex items-center gap-2 text-green-800">
                    <FiCheck className="size-5" />
                    <span className="font-medium">
                      Re-run started successfully!
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-green-700">
                    The page will refresh to show the updated status.
                  </p>
                </div>
              )}

              {/* Error Message */}
              {rerunError && (
                <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4">
                  <div className="flex items-center gap-2 text-red-800">
                    <FiAlertCircle className="size-5" />
                    <span className="font-medium">Failed to start re-run</span>
                  </div>
                  <p className="mt-1 text-sm text-red-700">{rerunError}</p>
                  <p className="mt-2 text-xs text-red-600">
                    Note: Re-run functionality requires a backend API endpoint.
                    Please ensure the worker infrastructure is set up to handle
                    re-run requests.
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setShowModal(false)}
                disabled={isRerunning}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRerun}
                disabled={
                  isRerunning || selectedRequests.size === 0 || rerunSuccess
                }
                className="flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
                title={
                  selectedRequests.size === 0
                    ? "Please select at least one request"
                    : ""
                }
              >
                {rerunSuccess ? (
                  <>
                    <FiCheck className="size-4" />
                    Re-run Started!
                  </>
                ) : isRerunning ? (
                  <>
                    <div className="size-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    Starting Re-run...
                  </>
                ) : (
                  <>
                    <FiRefreshCw className="size-4" />
                    Re-run Selected ({selectedRequests.size})
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

RerunButton.propTypes = {
  run: PropTypes.object.isRequired,
  onRerunStarted: PropTypes.func,
};

export default RerunButton;
